import { useState, useEffect, useCallback } from "react";
import { useGT } from "gt-react";
import { useServersStore, useSchemaStore, useAppStore, introspectSchema } from "../../store";
import { SchemaColumnBrowser } from "./SchemaColumnBrowser";
import { CatalogBrowser } from "./CatalogBrowser";
import { Search, RefreshCw, ExternalLink, X } from "lucide-react";

interface SchemaBrowserOverlayProps {
  standalone?: boolean;
  serverId?: string;
}

export function SchemaBrowserOverlay({ standalone = false, serverId: propServerId }: SchemaBrowserOverlayProps) {
  const t = useGT();
  const showSchemaBrowser = useAppStore((s) => s.showSchemaBrowser);
  const setShowSchemaBrowser = useAppStore((s) => s.setShowSchemaBrowser);
  const activeServerId = useServersStore((s) => s.activeServerId);
  const schemaCache = useSchemaStore((s) => s.schemaCache);
  const servers = useServersStore((s) => s.servers);

  const [filter, setFilter] = useState("");
  const [isIntrospecting, setIsIntrospecting] = useState(false);

  const effectiveServerId = propServerId ?? activeServerId;
  const schema = effectiveServerId ? schemaCache[effectiveServerId] : null;
  const server = servers.find((s) => s.id === effectiveServerId);
  const isGraphqlSchema = !schema?.catalogKind || schema.catalogKind === "graphql-schema";

  const visible = standalone || showSchemaBrowser;

  // Escape to close (non-standalone only)
  useEffect(() => {
    if (!visible || standalone) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowSchemaBrowser(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [visible, standalone, setShowSchemaBrowser]);

  const handleReload = useCallback(async () => {
    if (!effectiveServerId) return;
    setIsIntrospecting(true);
    try {
      await introspectSchema(effectiveServerId);
    } finally {
      setIsIntrospecting(false);
    }
  }, [effectiveServerId]);

  const handlePopOut = useCallback(() => {
    if (!effectiveServerId) return;
    const url = `${window.location.origin}${window.location.pathname}?view=schema&server=${effectiveServerId}`;
    window.open(url, "_blank", "width=1200,height=800");
    // Close the overlay after pop-out
    if (!standalone) {
      setShowSchemaBrowser(false);
    }
  }, [effectiveServerId, standalone, setShowSchemaBrowser]);

  if (!visible) return null;

  const content = (
    <div className={`flex flex-col bg-surface ${standalone ? "h-full" : "h-full w-full"}`}>
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface-raised px-4 py-2">
        <span className="text-sm font-semibold text-zinc-200">
          {isGraphqlSchema ? t("Schema Browser") : t("Catalog Browser")}
        </span>
        {server && (
          <span className="text-xs text-zinc-500">{server.name}</span>
        )}

        <div className="flex-1" />

        {/* Search */}
        <div className="relative w-64">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={
              isGraphqlSchema
                ? t("Filter types...")
                : schema?.catalogKind === "mongo-catalog"
                  ? t("Filter collections, fields, indexes...")
                  : t("Filter schemas, tables, columns...")
            }
            className="w-full rounded bg-surface-raised py-1.5 pl-8 pr-8 text-xs text-zinc-300 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
            autoFocus={!standalone}
          />
          {filter && (
            <button
              onClick={() => setFilter("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
            >
              <X size={10} />
            </button>
          )}
        </div>

        {/* Reload */}
        <button
          onClick={handleReload}
          disabled={isIntrospecting}
          className="rounded p-1.5 text-zinc-500 hover:bg-surface-overlay hover:text-zinc-300 disabled:opacity-40"
          title={t("Reload schema")}
        >
          <RefreshCw size={14} className={isIntrospecting ? "animate-spin" : ""} />
        </button>

        {/* Pop-out */}
        {!standalone && (
          <button
            onClick={handlePopOut}
            className="rounded p-1.5 text-zinc-500 hover:bg-surface-overlay hover:text-zinc-300"
            title={t("Open in new window")}
          >
            <ExternalLink size={14} />
          </button>
        )}

        {/* Close */}
        {!standalone && (
          <button
            onClick={() => setShowSchemaBrowser(false)}
            className="rounded p-1.5 text-zinc-500 hover:bg-surface-overlay hover:text-zinc-300"
            title={t("Close (Esc)")}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Body */}
      {!schema ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-zinc-600">
          <span className="text-sm">{t("No schema cached for this server")}</span>
          <button
            onClick={handleReload}
            disabled={isIntrospecting || !effectiveServerId}
            className="rounded bg-surface-overlay px-4 py-1.5 text-xs font-medium text-zinc-300 hover:bg-surface-overlay-hover disabled:opacity-40"
          >
            {isIntrospecting ? (isGraphqlSchema ? t("Introspecting...") : t("Refreshing...")) : (isGraphqlSchema ? t("Introspect Schema") : t("Refresh Catalog"))}
          </button>
        </div>
      ) : (
        isGraphqlSchema ? (
          <SchemaColumnBrowser
            introspectionJson={schema.introspectionJson}
            filter={filter}
          />
        ) : schema.catalogJson ? (
          <CatalogBrowser
            catalogKind={schema.catalogKind === "mongo-catalog" ? "mongo-catalog" : "postgres-catalog"}
            catalogJson={schema.catalogJson}
            filter={filter}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-zinc-600">
            {t("No catalog JSON cached for this server")}
          </div>
        )
      )}

      {/* Footer */}
      {schema && (
        <div className="flex shrink-0 items-center border-t border-border bg-surface-raised px-4 py-1">
          <span className="text-[10px] text-zinc-600">
            {t("Fetched")} {schema.fetchedAt ? new Date(schema.fetchedAt).toLocaleString() : t("unknown")}
          </span>
        </div>
      )}
    </div>
  );

  if (standalone) {
    return content;
  }

  // Overlay mode
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-stretch bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) setShowSchemaBrowser(false);
      }}
    >
      <div className="m-4 flex flex-1 flex-col overflow-hidden rounded-lg border border-border shadow-2xl">
        {content}
      </div>
    </div>
  );
}
